import api, { ExtendedAxiosRequestConfig } from 'api/axiosInstance';
import type { AxiosRequestConfig } from 'axios';
import qs from 'query-string';
import { ResponseType } from 'types/response.types';
import { UploadTypesEnum } from 'types/uploadEnums.types';

type UploadURLResponse = {
  url: { url: string; fields: { key: string; payload: string } };
};

const TIMEOUT_IN_MILLISECONDS = 20000;

let n = 1; // very important,
// it looks like safari can auto cache a response and re use it if a an identical request made again in very short time
// but we never need a cached response in this case
// so we are differentiating between thes requests with a new query param

export const getUploadURL = (
  params: {
    category: string;
    contentType: string;
  },
  config?: ExtendedAxiosRequestConfig,
): Promise<UploadURLResponse> =>
  api.get(`/v2/users/upload-url?${qs.stringify({ ...params, n: n++ })}`, {
    timeout: TIMEOUT_IN_MILLISECONDS,
    ...config,
  });

export const uploadFileToS3 = (
  { url, file, fields }: { url: string; file: Blob; fields: object },
  config?: ExtendedAxiosRequestConfig,
): Promise<any> => {
  const formData = new FormData();
  Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
  formData.append('file', file);
  return api
    .post(url, formData, {
      ...config,
      noHeaders: true,
      noOnBehalf: true,
    } as ExtendedAxiosRequestConfig)
    .then(() => {
      return fields;
    });
};

type UploadFileOptions = {
  file: File;
  onProgress?: (n: number) => void;
  category?: UploadTypesEnum;
  cancelToken?: AxiosRequestConfig['cancelToken'];
};

const unifyFileType = (fileType: string) => {
  if (fileType === ResponseType.textXml) return ResponseType.applicationXml;
  return fileType;
};

export const uploadFile = async ({
  file,
  onProgress,
  category,
  cancelToken,
}: UploadFileOptions) => {
  const {
    url: { url, fields },
  } = await getUploadURL(
    {
      category: category || UploadTypesEnum.document,
      contentType: unifyFileType(file.type),
    },
    { cancelToken },
  );
  return await uploadFileToS3(
    { url, fields, file },
    {
      onUploadProgress: (e) => onProgress?.((e.loaded / e.total) * 100),
      cancelToken,
    },
  ).then(() => fields);
};
